import { Box, Divider, Grid, Paper as MuiPaper, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import useWidth from '../../../../components/Hooks/useWidth';
import EmptyPlaceholder from '../../../../components/Table/EmptyPlaceholder';
import LangEnum from '../../../../lang/LangEnum';
import { useSignedContractsStore } from '../../../../stores/SignedContracts/SignedContracts.store';
import { formatDate } from '../../../../utils/dates';
import { EMPTY_STOCK_PATH } from '../../../../utils/images.constants';
import { useTranslation } from '../../../translation/TranslationContext';
import { useSignatureContext } from '../contexts/signature.context';
import { SignedContract } from '../signed-contract';
import ContractCellActions from './ContractCellActions';
import ContractHistoryActions from './ContractHistoryActions';

const Paper = withStyles(MuiPaper, (theme) => ({
  root: {
    borderRadius: theme.shape.borderRadius,
    boxShadow:
      '0px 2px 4px -1px rgba(0,0,0,0.1),0px 4px 5px 0px rgba(0,0,0,0.07),0px 1px 10px 0px rgba(0,0,0,0.06)',
  },
}));

type SignatureRendererProps = {
  signed: SignedContract;
  signingAs: 'BRAND' | 'STORE';
};

const SignatureRenderer = ({ signed, signingAs }: SignatureRendererProps) => {
  const { translate } = useTranslation();
  const label =
    signingAs === 'BRAND'
      ? translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_SIGNED_BRAND)
      : translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_SIGNED_STORE);

  const signature = signingAs === 'BRAND' ? signed.userBrand : signed.userStore;

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Typography color="textSecondary" variant="body2">
          {label}
        </Typography>
      </Grid>
      <Grid size={12}>
        <ContractCellActions
          breakLine={false}
          fullWidth
          contract={signed}
          signature={signature}
          signingAs={signingAs}
        />
      </Grid>
    </Grid>
  );
};

const getButtonSpacing = (smallWidth: boolean, mobileWidth: boolean) => {
  if (mobileWidth) {
    return 1;
  }

  if (smallWidth) {
    return 0;
  }

  return 1;
};

const ContractHistoryCard = ({ signed }: { signed: SignedContract }) => {
  const { translate } = useTranslation();
  const { smallWidth, mobileWidth } = useWidth();

  const buttonSpacing = getButtonSpacing(smallWidth, mobileWidth);

  return (
    <Box
      sx={{
        pt: 1.75,
        pb: 2,
      }}
    >
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid size="grow">
          <Grid container>
            <Grid size={12}>
              <Box
                sx={{
                  pl: 2,
                }}
              >
                <Typography variant="body2">
                  {translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_VERSION)} {signed.version}
                </Typography>
              </Box>
            </Grid>
            <Grid size={12}>
              <Box
                sx={{
                  pl: 2,
                }}
              >
                <Typography color="textSecondary" variant="body2">
                  {smallWidth
                    ? ''
                    : `${translate(LangEnum.ORDER_CONTRACT_HISTORY_TABLE_CELL_CREATED_AT)} `}
                  {formatDate(signed.createdAt, 'P p')}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid>
          <Box
            sx={{
              pr: 2,
            }}
          >
            <ContractHistoryActions signed={signed} />
          </Box>
        </Grid>
        <Box
          sx={{
            height: '6px',
            width: '100%',
          }}
        />
        <Grid size={12}>
          <Divider />
        </Grid>
        <Box
          sx={{
            height: '4px',
            width: '100%',
          }}
        />
        <Grid size={12}>
          <Grid container spacing={buttonSpacing}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Box
                sx={{
                  px: 2,
                }}
              >
                <SignatureRenderer signed={signed} signingAs="BRAND" />
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Box
                sx={{
                  px: 2,
                }}
              >
                <SignatureRenderer signed={signed} signingAs="STORE" />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const ContractHistoryCardList = () => {
  const { translate } = useTranslation();
  const { isLoading } = useSignatureContext();
  const { signedContracts } = useSignedContractsStore();
  const { smallWidth } = useWidth();

  if (isLoading) {
    return null;
  }

  if (signedContracts.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <EmptyPlaceholder
          width={200}
          height={200}
          src={EMPTY_STOCK_PATH}
          message={translate(LangEnum.ORDER_CONTRACT_HISTORY_EMPTY_LIST)}
          typographyProps={{
            style: {
              textTransform: 'lowercase',
              fontSize: smallWidth ? 20 : undefined,
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          alignItems: 'stretch',
        }}
      >
        {signedContracts.map((signed) => (
          <Grid key={signed.id} size={12}>
            <Paper
              elevation={4}
              style={{ height: '100%', borderRadius: 12, border: '0.5px solid rgba(0,0,0, 0.16)' }}
            >
              <ContractHistoryCard signed={signed} />
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          pb: 3,
        }}
      />
    </Box>
  );
};

export default ContractHistoryCardList;
